import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input } from '@artosera/ui';
import { demanderAccesSupport, revoquerAccesSupport } from '../../lib/api.js';
import { usePeut } from '../../auth/usePeut.js';
import { estElevationAnnulee, useElevation } from '../../components/useElevation.js';
import { formaterDate } from '../../lib/format.js';
import type { GalerieFiche, RoleAccesSupport } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import formStyles from '../../styles/form.module.css';

/**
 * Les droits qu'une demande peut porter, du moins large au plus large. La
 * Lecture reste le choix par défaut ; l'Équipe est ce qu'il faut pour régler
 * et publier le site d'une galerie, réservé à l'accès de support depuis le
 * 20 octobre 2026. La direction de la galerie voit ce choix avant d'accorder.
 */
const ROLES_SUPPORT: readonly RoleAccesSupport[] = ['lecture', 'equipe'];

export function SupportTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const peutDemander = peut('console:request-support-access');
  const queryClient = useQueryClient();
  const [motif, setMotif] = useState('');
  const [role, setRole] = useState<RoleAccesSupport>('lecture');
  // La demande d'accès de support demande un second facteur récent.
  const elevation = useElevation();
  const [erreurDemande, setErreurDemande] = useState<string | null>(null);

  const invalider = (): Promise<void> =>
    queryClient
      .invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] })
      .then(() => undefined);

  const demander = useMutation({
    mutationFn: () => elevation.executer(() => demanderAccesSupport(idGalerie, motif.trim(), role)),
    onMutate: () => setErreurDemande(null),
    onSuccess: async () => {
      setMotif('');
      setRole('lecture');
      await invalider();
    },
    onError: (cause: unknown) => {
      if (!estElevationAnnulee(cause) && cause instanceof Error) setErreurDemande(cause.message);
    },
  });

  const revoquer = useMutation({
    mutationFn: () => revoquerAccesSupport(idGalerie),
    onSuccess: () => invalider(),
  });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (motif.trim()) demander.mutate();
  };

  return (
    <div className={pageStyles.panneau}>
      <p>{t('fiche.support.texte')}</p>

      {galerie.supportAccess ? (
        <div className={pageStyles.banniere}>
          <span>
            <Badge tone={galerie.supportAccess.actif ? 'available' : 'reserved'}>
              {galerie.supportAccess.actif
                ? t('fiche.support.actif')
                : t('fiche.support.enAttente')}
            </Badge>{' '}
            {t('fiche.support.expireLe', {
              date: formaterDate(galerie.supportAccess.expireLe, '—'),
            })}
          </span>
          <Button
            variant="danger"
            size="sm"
            loading={revoquer.isPending}
            onClick={() => revoquer.mutate()}
          >
            {revoquer.isPending
              ? t('fiche.support.enCoursRevocation')
              : t('fiche.support.revoquer')}
          </Button>
        </div>
      ) : (
        <p className={pageStyles.videEtat}>{t('fiche.support.inactif')}</p>
      )}

      {/*
       * Refuser une demande de support n'est pas un geste de la console —
       * c'est la direction de la galerie qui accorde ou refuse
       * (`POST /v1/g/:slug/support-access/:id/refuser`, docs/dev/api.md §
       * 12.5). Une demande refusée redevient invisible ici (`supportAccess`
       * ne porte que « en attente » ou « accordé ») ; elle reste lisible dans
       * le Journal de cette galerie, sous « Accès de support refusé ».
       */}
      <p className={formStyles.aide}>{t('fiche.support.refusInfo')}</p>

      {peutDemander && !galerie.supportAccess ? (
        <form onSubmit={onSubmit}>
          <div className={formStyles.champ}>
            <label htmlFor="support-motif">{t('fiche.support.champMotif')}</label>
            <Input
              id="support-motif"
              required
              value={motif}
              onChange={(event) => setMotif(event.target.value)}
            />
          </div>
          <fieldset className={formStyles.choix}>
            <legend>{t('fiche.support.champRole')}</legend>
            {ROLES_SUPPORT.map((valeur) => (
              <label key={valeur} className={formStyles.option}>
                <input
                  type="radio"
                  name="support-role"
                  value={valeur}
                  checked={role === valeur}
                  onChange={() => setRole(valeur)}
                  aria-describedby={`support-role-${valeur}`}
                />
                <span>
                  {t(`fiche.support.roles.${valeur}`)}
                  <span id={`support-role-${valeur}`} className={formStyles.aide}>
                    {t(`fiche.support.aideRoles.${valeur}`)}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          {erreurDemande ? (
            <p className={formStyles.erreur} role="alert">
              {erreurDemande}
            </p>
          ) : null}
          <div className={formStyles.actionsFormulaire}>
            <Button type="submit" loading={demander.isPending} disabled={!motif.trim()}>
              {demander.isPending ? t('fiche.support.enCoursDemande') : t('fiche.support.demander')}
            </Button>
          </div>
        </form>
      ) : null}
      {elevation.dialogue}
    </div>
  );
}
